import { isArray, chain } from 'lodash';
import { mapState, mapGetters } from 'vuex';
import { ERROR_MAP, DOMAINS } from '@/utils/constants';
import { calculateVariation } from '@/utils/math';
import { secondsToTime, timeToSeconds, formatBlipHour } from '@/utils/formatter';
import blipRouterIcon from '@/assets/icons/blip_router.svg';
import blipBuilderIcon from '@/assets/icons/blip_builder.svg';
import blipRouterDarkThemeIcon from '@/assets/icons/blip_router_white.svg';
import blipBuilderDarkThemeIcon from '@/assets/icons/blip_builder_white.svg';

const mapIndexFromDimensions = (points) => {
  const mapped = points.reduce((map, point) => {
    if (!map[point.x]) {
      map[point.x] = 0;
    }
    map[point.x] += point.z;
    return map;
  }, {});
  return chain(mapped).toPairs().sortBy(1)
    .reverse().flatMap(d => d[0]).value();
};

export default {
  data() {
    return {
      iconsFromSVG: {
        master: blipRouterIcon,
        builder: blipBuilderIcon,
        masterDarkTheme: blipRouterDarkThemeIcon,
        builderDarkTheme: blipBuilderDarkThemeIcon,
      },
    };
  },
  computed: {
    ...mapState('tenant', [
      'tenants',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    ...mapGetters('filter', [
      'getStartDateInMilliseconds',
      'getEndDateInMilliseconds',
    ]),
    contractFilterItems() {
      const contractsSelected = this.tenants.map(tenant => ({ text: tenant.data.name, value: tenant.id, tenant }));
      return contractsSelected;
    },
    botFilterItems() {
      if (!Boolean(this.tempFilter.contracts?.value)) {
        return this.tenants
          .map(({ data }) => data.applications.map(bot => ({ ...bot, id: data.id })))
          .flat()
          .map(({ name, accessKey, type, id }) => ({
            text: name,
            value: accessKey,
            type,
            iconImage: this.getIconByTheme(type),
            contract: id,
          }));
      }

      return this.tenants
        .filter(({ id }) => this.tempFilter.contracts.value.includes(id))
        .map(({ data }) => data.applications.map(bot => ({ ...bot, id: data.id })))
        .flat()
        .map(({ name, accessKey, id, type, identity }) => ({
          text: name,
          value: accessKey,
          type,
          iconImage: this.getIconByTheme(type),
          contract: id,
          identity,
        }));
    },
    allBotFilterItems() {
      return this.tenants
        .map(({ data }) => data.applications.map(bot => ({ ...bot, id: data.id })))
        .flat()
        .map(({ name, accessKey, id, identity }) => ({
          text: name,
          value: accessKey,
          contract: id,
          identity,
        }));
    },
    aggregateData() {
      return (response, index) => {
        return response
          .map((response) => response[index].resource.items)
          .flat()
          .reduce((aggregationByDate, { intervalStart, count }) => {
            if (!Boolean(aggregationByDate[intervalStart])) {
              aggregationByDate[intervalStart] = 0;
            }

            aggregationByDate[intervalStart] += count;

            return aggregationByDate;
          }, {});
      };
    },
    domainsFilterItems () {
      return Object.keys(DOMAINS).map(key => ({
        text: DOMAINS[key],
        value: key,
      }));
    },
    computedContracts() {
      return Object.keys(this.contracts).length ? this.contracts : this.$store.state.filter.globalFilter.contracts;
    },
    computedBots() {
      return Object.keys(this.bots).length ? this.bots : this.$store.state.filter.globalFilter.bots;
    },
  },
  methods: {
    getIconByTheme(type) {
      return this.activeDashboard.data.isDarkTheme ? this.iconsFromSVG[`${type}DarkTheme`] : this.iconsFromSVG[type];
    },
    returnBlipSingleBotResponse(response) {
      return isArray(response) ? response[0] : response;
    },
    processResponseStackCount(responseStack) {
      return responseStack?.reduce((aggregatedCount, { resource, status, reason }) => {
        if (status !== 'success') throw new Error(JSON.stringify(reason.description));

        const count = resource.items?.reduce((aggregatedItemsCount, { count }) => aggregatedItemsCount + count, 0);
        return aggregatedCount + count;
      }, 0);
    },
    isSingleBotSelected(filter) {
      return !Boolean(filter?.bots?.value?.length > 1);
    },
    isBlipError(status) {
      return Boolean(ERROR_MAP.includes(status));
    },
    getBlipErrorDescription(status) {
      if (Boolean(ERROR_MAP.includes(status))) return this.$t(`mixins.blipModel.message.error.${status}`);
    },
    getMetricsFromActiveCampaign(series) {
      const metricNames = [
        { name: this.$t('components.BlipActiveCampaignComponents.audit'), value: 'total' },
        { name: this.$t('components.BlipActiveCampaignComponents.processed'), value: 'processed' },
        { name: this.$t('components.BlipActiveCampaignComponents.received'), value: 'received' },
        { name: this.$t('components.BlipActiveCampaignComponents.read'), value: 'read' },
      ];

      if (series.length === 0) {
        return [];
      }

      const metrics = {};

      for (const metric of metricNames) {
        metrics[metric.value] = { value: 0 };
      }

      for (const obj of series) {
        for (const item of obj.resource.items) {
          for (const metric of metricNames) {
            metrics[metric.value].value += item[metric.value];
          }
        }
      }

      const result = metricNames.map(metric => ({
        name: 'interactions',
        x: metric.name,
        y: 'Valor',
        z: metrics[metric.value].value,
        percentY: Number(this.calculateMetricsPercent(metrics[metric.value].value, metrics.total.value)),
      }));

      return result;
    },
    calculateMetricsPercent(metrics, total) {
      let result = (metrics / total) * 100;
      if (result % 1 !== 0) {
        result = result.toFixed(2);
      }
      return result;
    },
    getPropertyValue(obj, prop1, prop2) {
      return obj?.[prop1]?.[prop2] || null;
    },
    normalizeBlipFromPoints(points = [], hideNull = false) {
      const normalizedPoints = points.filter(p => {
        if (hideNull) {
          return p.x;
        }
        p.x = p.x || 'Indefinido';
        return p;
      });
      const mappedSeries = {};
      const indexes = mapIndexFromDimensions(normalizedPoints);
      const sumPoints = normalizedPoints.reduce((total, point) => total + point.z, 0);
      normalizedPoints.forEach(point => {
        if (!mappedSeries[point.y]) {
          mappedSeries[point.y] = {
            name: point.y,
            color: '#23b4d4',
            order: point.y,
            dataSorting: {
              enabled: true,
              sortOrder: 'asc',
              matchByName: true,
            },
            data: [],
          };
          if (point.percentY) {
            mappedSeries[point.y].dataLabels = {
              enabled: true,
              format: '{point.y}<br>{point.percentY}%',
            };
          }
        }
        const dataPoint = {
          name: point.x,
          x: indexes.indexOf(point.x),
          y: point.z,
        };

        dataPoint.percentY = point.percentY ? point.percentY : (point.z / sumPoints) * 100;

        mappedSeries[point.y].data.push(dataPoint);
      });

      return Object.values(mappedSeries);
    },
    responseTickets(response, ticketType, ticketType2) {
      const calculateTotal = (items, ticketType, ticketType2) =>
        items.reduce((acc, obj) => acc + obj[ticketType] + (ticketType2 ? obj[ticketType2] : 0), 0);

      const calculateTotalInData = (data, ticketType, ticketType2) =>
        data.reduce((acc, dataItem) => acc + calculateTotal(dataItem.resource.items, ticketType, ticketType2), 0);

      const currentValue = calculateTotalInData(response.currentData, ticketType, ticketType2);
      const previousValue = calculateTotalInData(response.previousData, ticketType, ticketType2);

      const variation = calculateVariation(previousValue, currentValue);
      this.value = currentValue;
      this.variation = variation;
      this.rawVariation = variation;
      this.comparationValue = previousValue;
    },

    formatIdentity(identity) {
      const nameRegex = /([^%]+)%40/gu;
      const match = nameRegex.exec(identity);

      if (match && match[1]) {
        const normalizedName = match[1]
          .split('.')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        return normalizedName;
      }
      return '';
    },
    processSelectItems(object, text = '', value = '') {
      if (Boolean(text) && Boolean(value)) {
        return {
          text: object[text],
          value: object[value],
        };
      }

      return {
        text: object,
        value: object,
      };
    },
    calculateComparisonPercentage(currentTotalTime, previousTotalTime) {
      const currentTotalSeconds = timeToSeconds(currentTotalTime);
      const previousTotalSeconds = timeToSeconds(previousTotalTime);

      const percentage = (((currentTotalSeconds - previousTotalSeconds) / previousTotalSeconds) * 100) || 0;

      return percentage.toFixed(2);
    },
    responseTimings(response, timingType) {
      const calculateTotalQueueTime = (queueData) => queueData
        .reduce((total, data) => total + timeToSeconds(data.resource[timingType]), 0);

      const sumQueueTime = (data) => secondsToTime(calculateTotalQueueTime(data)) || 0;

      const currentTotalTime = sumQueueTime(response.currentData);
      const previousTotalTime = sumQueueTime(response.previousData);

      const variation = this.calculateComparisonPercentage(currentTotalTime, previousTotalTime);

      this.value = currentTotalTime;
      this.variation = variation;
      this.rawVariation = variation;
      this.comparationValue = previousTotalTime;
    },
    responseNotAggregateTimings(response, type) {
      const { currentData, previousData } = response;
      let currentValue = null;
      let previousValue = null;

      for (let i = 0; i < currentData.length; i++) {
        const resource = currentData[i].resource;
        if (resource.hasOwnProperty(type)) {
          currentValue = resource[type];
          break;
        }
      }

      for (let i = 0; i < previousData.length; i++) {
        const resource = previousData[i].resource;
        if (resource.hasOwnProperty(type)) {
          previousValue = resource[type];
          break;
        }
      }

      currentValue = formatBlipHour(currentValue);
      previousValue = formatBlipHour(previousValue);

      const variation = this.calculateComparisonPercentage(currentValue, previousValue);

      return {
        currentValue,
        previousValue,
        variation,
      };
    },
    formatBlipHour(time) {
      if (time !== undefined) {
        const formattedTime = time.replace(/\.\d+$/u, '');

        if (formattedTime.includes('.')) {
          const [days, rest] = formattedTime.split('.');
          const [hours, minutes] = rest.split(':');
          return `${days}d ${hours}:${minutes}`;
        }
        return formattedTime;
      }
      return '-';
    },
    getBotId(to) {
      const parts = to.split('/!');
      if (parts.length === 2) {
        return parts[0];
      }
      return null;
    },
    getBotName(to, cardFilters) {
      const regex = /^([^@]+)@/u;
      const botId = this.getBotId(to);
      const contract = cardFilters?.contracts?.value;

      const contractFind = this.tenants.find((item) => {
        return item.id === contract;
      });

      if (contractFind) {
        const bot = contractFind.data.applications.find((application) => {
          return application.identity === botId;
        });

        return bot ? bot.name : botId.match(regex)[1];
      }
    },
    processSelectItems(object, text = '', value = '') {
      if (Boolean(text) && Boolean(value)) {
        return {
          text: object[text],
          value: object[value],
        };
      }

      return {
        text: object,
        value: object,
      };
    },
    totalResponseItems(response) {
      return response.map((item) => item.resource
        ? item.resource.items
          ? item.resource.items
          : item.resource
        : []
      ).every(arr => arr.length === 0);
    },
    verifyItemsNull(response) {
      return response.every(item => item.resource.items === null);
    },
    filterItemsNull(response) {
      return response.filter(item => item.resource.items !== null);
    },
  },
};
